import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import SearchableSelect from "../ui/SearchableSelect";
import { DollarSign, TrendingUp, Briefcase, Users, Wrench, Download, CreditCard, ExternalLink, FileDown, Loader2, Handshake, Mail, CheckCircle2, X, Coins, Receipt, Settings2, Columns3 } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import StatCard from "./StatCard";
import EmailReportDialog from "./EmailReportDialog";
import ColumnCustomizerDialog from "./ColumnCustomizerDialog";

// Registry of every available column for the Job Details table. Order below =
// default order; `optional` columns start hidden. Saved layouts live on
// business_settings.technician_report_columns as ordered [{id, visible}].
export const TECH_JOB_COLUMN_DEFS = [
  { id: "job_id", label: "Job ID" },
  { id: "technician", label: "Tech" },
  { id: "date", label: "Date" },
  { id: "full_address", label: "Full Address" },
  { id: "job_type", label: "Job Type" },
  { id: "subtotal", label: "Sub Total", right: true, title: "Customer total — includes tax and credit fee" },
  { id: "payment_by", label: "Payment By" },
  { id: "tech_collected", label: "Tech Collected" },
  { id: "parts", label: "Parts", right: true },
  { id: "tech_commission", label: "Tech Comm.", right: true },
  { id: "tip", label: "Tip", right: true, title: "Card tips collected via the tech portal — not part of payout math" },
  { id: "total", label: "Total", right: true, title: "Net amount without taxes and fees — used for commission calculation" },
  { id: "tax", label: "Tax", right: true, title: "Sales tax on the job — excluded from technician commission" },
  { id: "credit_fee", label: "Fee", right: true, title: "Credit card fee charged to the customer on the invoice" },
  { id: "source_commission", label: "Source Comm.", right: true },
  { id: "company_profit", label: "Co. Profit", right: true },
  { id: "address", label: "Address (street)", optional: true },
  { id: "zip", label: "ZIP", optional: true },
  { id: "comm_rate", label: "Comm. Rate", right: true, optional: true },
  { id: "processing_fees", label: "Processing Fees", right: true, optional: true },
  { id: "customer_name", label: "Customer", optional: true },
];
const DEFAULT_COL_CONFIG = TECH_JOB_COLUMN_DEFS.map((c) => ({ id: c.id, visible: !c.optional }));
const COL_REGISTRY = Object.fromEntries(TECH_JOB_COLUMN_DEFS.map((c) => [c.id, c.label]));
const COL_BY_ID = Object.fromEntries(TECH_JOB_COLUMN_DEFS.map((c) => [c.id, c]));
// Merge a saved layout with the registry: keep saved order/visibility/custom
// labels, drop unknown ids, append any new registry columns as hidden.
const mergeColConfig = (saved) => {
  if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_COL_CONFIG;
  const seen = new Set();
  const out = [];
  saved.forEach((c) => {
    const id = c && c.id;
    if (id && COL_BY_ID[id] && !seen.has(id)) {
      seen.add(id);
      const label = typeof c.label === "string" && c.label.trim() ? c.label.trim() : null;
      out.push({ id, visible: c.visible !== false, label });
    }
  });
  TECH_JOB_COLUMN_DEFS.forEach((c) => { if (!seen.has(c.id)) out.push({ id: c.id, visible: false, label: null }); });
  return out;
};
const fullAddressOf = (job) => {
  const addr = job.address || "";
  if (!addr) return "";
  const zip = job.zip && !addr.includes(job.zip) ? `, ${job.zip}` : "";
  return `${addr}${zip}`;
};

const TechnicianReportTab = ({
  technicianData, selectedTechnician, setSelectedTechnician,
  formatCurrency, formatDate, exportToPDF, exportToCSV, exportingPdf, openSettle,
  emailTechnicianReportPdf, dateFilter,
  selectedJobKeys, setSelectedJobKeys, openSettleForJobs, jobEntryBalance, onOpenJob,
}) => {
  const { isDark } = useTheme();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // PDF template picker — templates are managed in Settings → PDF Reports.
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  useEffect(() => {
    axios.get(`${API}/report-templates`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((res) => setPdfTemplates(res.data.templates || []))
      .catch(() => {});
  }, []);
  // Column layout — account-wide, stored on business settings.
  const [colConfig, setColConfig] = useState(DEFAULT_COL_CONFIG);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [savingCols, setSavingCols] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/business`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((res) => setColConfig(mergeColConfig(res.data?.technician_report_columns)))
      .catch(() => {});
  }, []);
  const saveColumns = async (cols) => {
    setSavingCols(true);
    try {
      await axios.put(`${API}/settings/business`, { technician_report_columns: cols },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setColConfig(cols);
      setCustomizerOpen(false);
      toast.success("Table layout saved");
    } catch {
      toast.error("Could not save table layout");
    }
    setSavingCols(false);
  };
  const visibleCols = colConfig.filter((c) => c.visible).map((c) => {
    const def = COL_BY_ID[c.id];
    if (!def) return null;
    return c.label ? { ...def, label: c.label } : def;
  }).filter(Boolean);
  const userRole = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role; } catch { return null; } })();
  const canCustomize = !userRole || ["owner", "admin"].includes(userRole);
  if (!technicianData) return null;

  const { summary, jobs, technicians, payments_by_type } = technicianData;
  const profitColor = isDark ? "text-[#FFCC00]" : "text-amber-600";
  const profitBg = isDark ? "bg-[#FFCC00]/10" : "bg-amber-100";
  const linkColor = isDark ? "text-[#FFCC00] hover:text-[#FFCC00]/80" : "text-blue-600 hover:text-blue-800";
  const btnOutline = isDark ? "border-white/20 text-foreground hover:bg-theme-hover" : "border-gray-300 text-gray-700 hover:bg-gray-100";
  const pdfBtnOutline = isDark ? "border-[#FFCC00]/50 text-[#FFCC00] hover:bg-[#FFCC00]/10" : "border-amber-500/50 text-amber-700 hover:bg-amber-50";

  // Selected tech metadata for the email dialog. Only a real tech (not the
  // "All Technicians" aggregate) can be emailed — the button is disabled
  // when selectedTechnician === "all".
  const selectedTechObj = (technicians || []).find(t => t.member_id === selectedTechnician);
  const canEmail = selectedTechnician && selectedTechnician !== "all";

  const periodLabel = (() => {
    if (!dateFilter) return "the selected period";
    const map = {
      today: "today", yesterday: "yesterday",
      this_week: "this week", last_week: "last week",
      this_month: "this month", last_month: "last month",
      this_year: "this year", last_year: "last year",
      all: "all time", custom: "the selected range",
    };
    return map[dateFilter] || dateFilter.replace(/_/g, " ");
  })();

  // ─── Selective settlement (checkbox) helpers ───
  const keyOf = (j) => `${j.job_id}::${j.technician_id || ""}`;
  const selectable = (jobs || []).filter(j => !j.settled && j.technician_id);
  const selectedEntries = Object.values(selectedJobKeys || {});
  const selectedCount = selectedEntries.length;
  const selectedTechIds = [...new Set(selectedEntries.map(j => j.technician_id))];
  const singleTech = selectedTechIds.length === 1;
  const selectedTotal = selectedEntries.reduce((s, j) => s + (jobEntryBalance ? jobEntryBalance(j) : 0), 0);
  const allSelected = selectable.length > 0 && selectable.every(j => (selectedJobKeys || {})[keyOf(j)]);

  const toggleAll = () => {
    if (allSelected) { setSelectedJobKeys({}); return; }
    const next = {};
    selectable.forEach(j => { next[keyOf(j)] = j; });
    setSelectedJobKeys(next);
  };
  const toggleOne = (j) => {
    const k = keyOf(j);
    setSelectedJobKeys(prev => {
      const next = { ...prev };
      if (next[k]) delete next[k]; else next[k] = j;
      return next;
    });
  };

  // ── Dynamic column rendering (order/visibility from colConfig) ──
  const renderCell = (col, job, i) => {
    switch (col.id) {
      case "job_id":
        return (
          <TableCell key={col.id}>
            <button
              type="button"
              onClick={() => onOpenJob && onOpenJob(job)}
              className={`${linkColor} flex items-center gap-1 font-mono text-xs`}
              data-testid={`tech-job-link-${job.job_id}`}
            >
              {job.job_id?.slice(-8) || "—"}<ExternalLink className="w-3 h-3" />
            </button>
          </TableCell>
        );
      case "technician":
        return <TableCell key={col.id} className="text-foreground font-medium">{job.technician_name || "—"}</TableCell>;
      case "date":
        return <TableCell key={col.id} className="text-muted-foreground text-sm">{formatDate(job.completed_at)}</TableCell>;
      case "full_address":
        return (
          <TableCell key={col.id} className="text-muted-foreground text-sm min-w-[160px] max-w-[220px] whitespace-normal leading-snug" data-testid={`full-address-${job.job_id}-${job.technician_id || i}`}>
            {fullAddressOf(job) || "N/A"}
          </TableCell>
        );
      case "address":
        return <TableCell key={col.id} className="text-muted-foreground text-sm max-w-[150px] truncate">{job.address || "N/A"}</TableCell>;
      case "zip":
        return <TableCell key={col.id} className="text-muted-foreground">{job.zip || "—"}</TableCell>;
      case "job_type":
        return <TableCell key={col.id} className="text-foreground">{job.job_type}</TableCell>;
      case "customer_name":
        return <TableCell key={col.id} className="text-foreground text-sm max-w-[140px] truncate">{job.customer_name || "—"}</TableCell>;
      case "subtotal":
        return (
          <TableCell key={col.id} className="text-foreground text-right font-medium" title={col.title} data-testid={`subtotal-cell-${job.job_id}-${job.technician_id || i}`}>
            {formatCurrency(job.subtotal ?? job.total_with_tax ?? job.total ?? 0)}
          </TableCell>
        );
      case "total":
        return <TableCell key={col.id} className="text-green-600 text-right font-medium" title={col.title}>{formatCurrency(job.total)}</TableCell>;
      case "tax":
        return (
          <TableCell key={col.id} className="text-teal-600 text-right text-sm" title={col.title} data-testid={`tax-cell-${job.job_id}-${job.technician_id || i}`}>
            {(job.tax || 0) > 0 ? formatCurrency(job.tax) : "—"}
          </TableCell>
        );
      case "credit_fee":
        return (
          <TableCell key={col.id} className="text-teal-600 text-right text-sm" title={col.title} data-testid={`credit-fee-cell-${job.job_id}-${job.technician_id || i}`}>
            {(job.credit_fee || 0) > 0 ? formatCurrency(job.credit_fee) : "—"}
          </TableCell>
        );
      case "tip":
        return (
          <TableCell key={col.id} className={`text-right ${(job.tip || 0) > 0 ? "text-emerald-500 font-medium" : "text-muted-foreground/50"}`} title={col.title} data-testid={`tip-cell-${job.job_id}-${job.technician_id || i}`}>
            {(job.tip || 0) > 0 ? formatCurrency(job.tip) : "—"}
          </TableCell>
        );
      case "payment_by":
        return <TableCell key={col.id} className="text-muted-foreground text-sm">{job.payment_by}</TableCell>;
      case "tech_collected":
        return (
          <TableCell
            key={col.id}
            className={`text-sm font-medium ${job.is_primary_tech && job.tech_collected > 0 ? "text-amber-500" : "text-muted-foreground/50"}`}
            data-testid={`tech-collected-${job.job_id}`}
          >
            {job.is_primary_tech && job.tech_collected > 0 ? formatCurrency(job.tech_collected) : "—"}
          </TableCell>
        );
      case "parts":
        return <TableCell key={col.id} className="text-orange-500 text-right">{formatCurrency(job.parts)}</TableCell>;
      case "processing_fees":
        return <TableCell key={col.id} className="text-purple-500 text-right">{formatCurrency(job.processing_fees)}</TableCell>;
      case "comm_rate":
        return <TableCell key={col.id} className="text-muted-foreground text-right text-sm" data-testid={`comm-rate-${job.job_id}-${job.technician_id || i}`}>{job.commission_rate || "—"}</TableCell>;
      case "tech_commission":
        return <TableCell key={col.id} className="text-cyan-600 text-right">{formatCurrency(job.tech_commission)}</TableCell>;
      case "source_commission":
        return <TableCell key={col.id} className="text-pink-500 text-right">{formatCurrency(job.source_commission)}</TableCell>;
      case "company_profit":
        return <TableCell key={col.id} className={`${profitColor} text-right font-semibold`}>{formatCurrency(job.company_profit)}</TableCell>;
      default:
        return <TableCell key={col.id}>—</TableCell>;
    }
  };

  // CSV export mirrors the visible column layout.
  const csvValue = (id, job) => {
    switch (id) {
      case "job_id": return job.job_id?.slice(-8) || "";
      case "technician": return job.technician_name || "";
      case "date": return formatDate(job.completed_at) || "";
      case "full_address": return fullAddressOf(job);
      case "address": return job.address || "";
      case "zip": return job.zip || "";
      case "job_type": return job.job_type || "";
      case "customer_name": return job.customer_name || "";
      case "subtotal": return job.subtotal ?? job.total_with_tax ?? job.total ?? 0;
      case "total": return job.total || 0;
      case "tax": return job.tax || 0;
      case "credit_fee": return job.credit_fee || 0;
      case "tip": return job.tip || 0;
      case "payment_by": return job.payment_by || "";
      case "tech_collected": return job.is_primary_tech ? (job.tech_collected || 0) : 0;
      case "parts": return job.parts || 0;
      case "processing_fees": return job.processing_fees || 0;
      case "comm_rate": return job.commission_rate || "";
      case "tech_commission": return job.tech_commission || 0;
      case "source_commission": return job.source_commission || 0;
      case "company_profit": return job.company_profit || 0;
      default: return "";
    }
  };
  const handleExportCsv = () => exportToCSV(
    (jobs || []).map((j) => {
      const row = {};
      visibleCols.forEach((c) => { row[c.label] = csvValue(c.id, j); });
      return row;
    }),
    "technician_report"
  );

  return (
    <div className="space-y-6">
      {/* Technician Filter */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground text-sm">Technician:</span>
        </div>
        {/* Searchable Technician picker — adds an inline search input inside
            the dropdown once the list has 4+ techs. Same API as the previous
            shadcn Select so onChange / value contracts are unchanged. */}
        <SearchableSelect
          value={selectedTechnician}
          onChange={setSelectedTechnician}
          options={[
            { value: "all", label: `All Technicians (${technicianData.job_counts_total ?? summary.total_jobs})` },
            ...(technicians?.map(t => ({
              value: t.member_id,
              label: `${t.name} (${(technicianData.job_counts || {})[t.member_id] || 0})`,
            })) || []),
          ]}
          placeholder="All Technicians"
          searchThreshold={4}
          className="w-[200px]"
          triggerClassName="bg-theme-surface border-theme text-foreground h-10"
          data-testid="technician-filter"
        />
      </div>

      {/* Summary Cards — responsive grid: 8 cards wrap as 4 + 4 on
          lg/xl tablets/small laptops so the StatCard's `truncate` doesn't
          chop off long labels (e.g. "Processing Fees", "Source Commission",
          "Company Profit"). Single-row layout only at 2xl ≥ 1536 px. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-8 gap-3">
        <StatCard title="Total Jobs" value={summary.total_jobs} icon={Briefcase} color="text-blue-500" bgColor="bg-blue-500/10" />
        <StatCard title="Total Revenue" value={formatCurrency(summary.total_revenue)} icon={DollarSign} color="text-green-600" bgColor="bg-green-500/10" />
        <StatCard title="Parts Cost" value={formatCurrency(summary.total_parts)} icon={Wrench} color="text-orange-500" bgColor="bg-orange-500/10" />
        <StatCard title="Tech Parts" value={formatCurrency(summary.tech_parts)} icon={Wrench} color="text-amber-500" bgColor="bg-amber-500/10" />
        <StatCard title="Processing Fees" value={formatCurrency(summary.total_processing_fees)} icon={CreditCard} color="text-purple-500" bgColor="bg-purple-500/10" />
        <StatCard title="Tech Payout" value={formatCurrency(summary.total_tech_payout)} icon={Users} color="text-cyan-600" bgColor="bg-cyan-500/10" />
        <StatCard title="Source Commission" value={formatCurrency(summary.total_source_commission)} icon={Briefcase} color="text-pink-500" bgColor="bg-pink-500/10" />
        <StatCard title="Company Profit" value={formatCurrency(summary.net_company_profit)} icon={TrendingUp} color={profitColor} bgColor={profitBg} />
        {(summary.total_tax_charged || 0) > 0 && (
          <>
            <StatCard title="Tax Charged" value={formatCurrency(summary.total_tax_charged)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="tech-tax-charged-card" subtitle="Excluded from commission" />
            <StatCard title="Tax Collected" value={formatCurrency(summary.total_tax_collected)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="tech-tax-collected-card" subtitle="From payments received" />
          </>
        )}
      </div>

      {/* Payments by Type */}
      {payments_by_type && payments_by_type.length > 0 && (
        <Card className="glass-card border-theme">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <CreditCard className={`w-4 h-4 ${profitColor}`} />
              Payments by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {payments_by_type.map((pt, i) => (
                <div key={i} className="bg-theme-surface rounded-lg px-4 py-2 border border-theme" data-testid={`tech-pbt-${i}`}>
                  <p className="text-foreground font-medium">{pt.name}</p>
                  <p className={`${profitColor} font-semibold`}>{formatCurrency(pt.amount)}</p>
                  <p className="text-muted-foreground text-xs">{pt.count} transactions</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips by Technician — card tips collected on-site via the tech
          portal. Display-only: NOT part of payout/settlement math. */}
      {(summary.total_tips || 0) > 0 && (
        <Card className="glass-card border-theme" data-testid="tips-by-tech-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <Coins className="w-4 h-4 text-emerald-500" />
              Tips Collected
              <span className="ml-auto text-emerald-500 font-semibold" data-testid="tips-total">{formatCurrency(summary.total_tips)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {(technicianData.tech_balances || []).filter(tb => (tb.tips_total || 0) > 0).map((tb) => (
                <div key={tb.technician_id} className="bg-theme-surface rounded-lg px-4 py-2 border border-theme" data-testid={`tips-tech-${tb.technician_id}`}>
                  <p className="text-foreground font-medium">{tb.name}</p>
                  <p className="text-emerald-500 font-semibold">{formatCurrency(tb.tips_total)}</p>
                  <p className="text-muted-foreground text-xs">{tb.tips_count} tip{tb.tips_count === 1 ? "" : "s"}</p>
                </div>
              ))}
            </div>
            <p className="text-muted-foreground text-xs mt-3">
              Card tips collected on-site via the tech portal. Tips are not included in payout math — add a payout adjustment to include them in a settlement.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Jobs Table */}
      <Card className="glass-card border-theme">
        <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-foreground text-base font-['Manrope']">Technician Job Details</CardTitle>
          <div className="flex flex-wrap gap-2">
            {pdfTemplates.length > 0 && (
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="h-9 w-[190px] text-xs bg-theme-surface border-theme" data-testid="pdf-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    {(() => { const d = pdfTemplates.find(t => t.is_default); return d ? `Default: ${d.name}` : "Default (standard PDF)"; })()}
                  </SelectItem>
                  <SelectItem value="standard">Standard (built-in)</SelectItem>
                  {pdfTemplates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canCustomize && (
              <Button variant="outline" size="sm" onClick={() => setCustomizerOpen(true)} className={`${btnOutline} flex-1 sm:flex-initial`} data-testid="customize-columns-btn">
                <Columns3 className="w-4 h-4 mr-2" /> Columns
              </Button>
            )}
            {canCustomize && (
              <Button variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-foreground"
                title="Customize the PDF layout (Settings → PDF Reports)"
                onClick={() => { window.location.href = "/dashboard/settings?tab=pdf-reports"; }}
                data-testid="customize-pdf-btn">
                <Settings2 className="w-4 h-4" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => exportToPDF('technician', { templateId: selectedTemplate })} disabled={exportingPdf === 'technician'} className={`${pdfBtnOutline} flex-1 sm:flex-initial min-w-[110px]`} data-testid="export-technician-pdf-btn">
              {exportingPdf === 'technician' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
            {emailTechnicianReportPdf && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEmailDialogOpen(true)}
                disabled={!canEmail}
                className={`${pdfBtnOutline} flex-1 sm:flex-initial min-w-[110px]`}
                data-testid="email-technician-pdf-btn"
                title={canEmail ? `Email PDF to ${selectedTechObj?.email || "technician"}` : "Pick a specific technician to email their report"}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email PDF
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExportCsv} className={`${btnOutline} flex-1 sm:flex-initial min-w-[110px]`} data-testid="export-technician-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Selection bar — appears when jobs are checked for settlement */}
          {selectedCount > 0 && (
            <div
              className={`mb-3 flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${isDark ? "bg-cyan-500/10 border-cyan-500/30" : "bg-cyan-50 border-cyan-200"}`}
              data-testid="settle-selection-bar"
            >
              <span className="text-sm font-medium text-foreground" data-testid="settle-selection-count">
                {selectedCount} job{selectedCount > 1 ? "s" : ""} selected
              </span>
              <span className={`text-sm font-semibold ${selectedTotal > 0 ? "text-red-500" : selectedTotal < 0 ? "text-green-600" : "text-muted-foreground"}`} data-testid="settle-selection-total">
                {selectedTotal > 0
                  ? `Tech owes ${formatCurrency(Math.abs(selectedTotal))}`
                  : selectedTotal < 0
                    ? `Pay tech ${formatCurrency(Math.abs(selectedTotal))}`
                    : "$0.00 balance"}
              </span>
              {!singleTech && (
                <span className="text-xs text-amber-600 font-medium" data-testid="settle-multi-tech-warning">
                  Select jobs from one technician at a time to settle
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelectedJobKeys({})} data-testid="clear-selection-btn">
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs bg-cyan-600 hover:bg-cyan-700 text-white"
                  disabled={!singleTech}
                  onClick={() => openSettleForJobs(selectedEntries)}
                  data-testid="settle-selected-btn"
                >
                  <Handshake className="w-3.5 h-3.5 mr-1" /> Settle Selected ({selectedCount})
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-theme hover:bg-transparent">
                  <TableHead className="w-[36px]">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      disabled={selectable.length === 0}
                      aria-label="Select all unsettled jobs"
                      data-testid="select-all-jobs-checkbox"
                    />
                  </TableHead>
                  {visibleCols.map((c) => (
                    <TableHead key={c.id} className={`text-muted-foreground ${c.right ? "text-right" : ""}`} title={c.title}>
                      {c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow className="border-theme">
                    <TableCell colSpan={visibleCols.length + 1} className="text-center text-muted-foreground py-8">No jobs found for selected period</TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job, i) => (
                    <TableRow key={`${job.job_id}-${job.technician_id || i}`} className={`border-theme hover:bg-theme-surface ${job.settled ? "opacity-60" : ""}`}>
                      <TableCell className="w-[36px]">
                        {job.settled ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 uppercase"
                            title={job.settled_at ? `Settled ${formatDate(job.settled_at)}` : "Settled"}
                            data-testid={`settled-badge-${job.job_id}-${job.technician_id || i}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </span>
                        ) : job.technician_id ? (
                          <Checkbox
                            checked={!!(selectedJobKeys || {})[keyOf(job)]}
                            onCheckedChange={() => toggleOne(job)}
                            aria-label={`Select job ${job.job_id?.slice(-8)}`}
                            data-testid={`job-checkbox-${job.job_id}-${job.technician_id || i}`}
                          />
                        ) : (
                          <Checkbox
                            disabled
                            title="No technician assigned to this job — open the job to assign one, then it can be settled"
                            aria-label="No technician assigned"
                            data-testid={`job-checkbox-disabled-${job.job_id}`}
                          />
                        )}
                      </TableCell>
                      {visibleCols.map((c) => renderCell(c, job, i))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Tech Balances Summary */}
      {technicianData.tech_balances && technicianData.tech_balances.length > 0 && (
        <Card className="glass-card border-theme">
          <CardHeader>
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <Handshake className="w-4 h-4 text-cyan-500" /> Technician Balances
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="w-full overflow-x-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}>
              <Table className="min-w-[1120px]">
                <TableHeader>
                  <TableRow className="border-theme hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Technician</TableHead>
                    <TableHead className="text-muted-foreground text-right">Jobs</TableHead>
                    <TableHead className="text-muted-foreground text-right">Commission</TableHead>
                    <TableHead className="text-muted-foreground text-right">Fees</TableHead>
                    <TableHead className="text-muted-foreground text-right">Adjust.</TableHead>
                    <TableHead className="text-muted-foreground text-right">Parts Reimb.</TableHead>
                    <TableHead className="text-muted-foreground text-right">Payout</TableHead>
                    <TableHead className="text-muted-foreground text-right">Collected</TableHead>
                    <TableHead className="text-muted-foreground text-right" title="Card tips collected via the tech portal — not included in payout">Tips</TableHead>
                    <TableHead className="text-muted-foreground text-right">Owes</TableHead>
                    <TableHead className="text-muted-foreground text-right">Settled</TableHead>
                    <TableHead className="text-muted-foreground text-right">Balance</TableHead>
                    <TableHead className="text-muted-foreground text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {technicianData.tech_balances.map((tb) => {
                    const bal = tb.remaining_balance || 0;
                    return (
                      <TableRow key={tb.technician_id} className="border-theme hover:bg-theme-surface">
                        <TableCell className="text-foreground font-medium">{tb.name}</TableCell>
                        <TableCell className="text-muted-foreground text-right">{tb.jobs_count}</TableCell>
                        <TableCell className="text-purple-500 text-right">{formatCurrency(tb.total_commission)}</TableCell>
                        <TableCell className="text-red-500 text-right">{formatCurrency(tb.processing_fees)}</TableCell>
                        <TableCell className={`text-right font-medium ${(tb.total_adjustments || 0) > 0 ? 'text-green-500' : (tb.total_adjustments || 0) < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                          {(tb.total_adjustments || 0) !== 0 ? `${(tb.total_adjustments || 0) > 0 ? '+' : '-'}${formatCurrency(Math.abs(tb.total_adjustments))}` : '$0.00'}
                        </TableCell>
                        <TableCell className={`text-right ${(tb.parts_reimbursement || 0) > 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                          {(tb.parts_reimbursement || 0) > 0 ? `+${formatCurrency(tb.parts_reimbursement)}` : '$0.00'}
                        </TableCell>
                        <TableCell className={`${profitColor} text-right font-medium`}>{formatCurrency(tb.net_payout)}</TableCell>
                        <TableCell className="text-blue-500 text-right">{formatCurrency(tb.tech_collected || 0)}</TableCell>
                        <TableCell
                          className={`text-right ${(tb.tips_total || 0) > 0 ? "text-emerald-500 font-medium" : "text-muted-foreground/50"}`}
                          title="Card tips collected via the tech portal — not included in payout"
                          data-testid={`tips-cell-${tb.technician_id}`}
                        >
                          {(tb.tips_total || 0) > 0 ? formatCurrency(tb.tips_total) : "—"}
                        </TableCell>
                        <TableCell className={`text-right ${(tb.owes_company || 0) > 0 ? 'text-red-500' : (tb.owes_company || 0) < 0 ? 'text-green-500' : (tb.total_settled || 0) !== 0 ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`} data-testid={`owes-cell-${tb.technician_id}`}>
                          {(tb.owes_company || 0) !== 0 ? `${(tb.owes_company || 0) > 0 ? '' : '-'}${formatCurrency(Math.abs(tb.owes_company))}` : '$0.00'}
                        </TableCell>
                        <TableCell className="text-cyan-500 text-right">{formatCurrency(tb.total_settled || 0)}</TableCell>
                        <TableCell className={`text-right font-semibold ${bal > 0 ? 'text-red-500' : bal < 0 ? 'text-green-500' : 'text-emerald-600'}`}>
                          {bal !== 0
                            ? `${bal > 0 ? '' : '-'}${formatCurrency(Math.abs(bal))}`
                            : (tb.total_settled !== 0 ? <span className="inline-flex items-center gap-1 text-xs"><Handshake className="w-3 h-3" /> Settled</span> : '$0.00')}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-theme hover:bg-theme-surface" onClick={() => openSettle(tb)} data-testid={`settle-btn-${tb.technician_id}`}>
                            <Handshake className="w-3.5 h-3.5 mr-1" /> Settle
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Column customizer for the Job Details table */}
      <ColumnCustomizerDialog
        open={customizerOpen}
        onOpenChange={setCustomizerOpen}
        columns={colConfig}
        registry={COL_REGISTRY}
        defaults={DEFAULT_COL_CONFIG}
        onSave={saveColumns}
        saving={savingCols}
      />

      {/* Email Report dialog (only mounted when we have the handler) */}
      {emailTechnicianReportPdf && (
        <EmailReportDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          technicianName={selectedTechObj?.name}
          technicianEmail={selectedTechObj?.email}
          periodLabel={periodLabel}
          onSubmit={({ toEmail, subject, bodyHtml }) =>
            emailTechnicianReportPdf({
              technicianId: selectedTechnician,
              toEmail,
              subject,
              bodyHtml,
              templateId: selectedTemplate,
            })
          }
        />
      )}
    </div>
  );
};

export default TechnicianReportTab;
